import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  AddNewButton,
  Button,
  Card,
  Divider,
  Dropdown,
  Icon,
  MultipleDropdown,
  Search,
  TableSkeleton,
  Tabs,
  toggleObject,
  useLocalStorage,
} from '@makeably/creativex-design-system';
import DuplicateGuideline from 'components/internal/guidelines/DuplicateGuideline';
import GuidelineActions from 'components/internal/guidelines/GuidelineActions';
import { parseGuidelineTypeLabel } from 'components/internal/guidelines/GuidelineDisplay';
import { findRoleOption } from 'components/internal/guidelines/GuidelineForm';
import {
  NULL_DIMENSION,
  NULL_VALUE,
  emptyState,
  filterProps,
  getBoolDimension,
  getDateDimension,
} from 'components/internal/shared';
import FilterTags from 'components/molecules/FilterTags';
import ItemsFilter from 'components/molecules/ItemsFilter';
import ItemsTable from 'components/molecules/ItemsTable';
import { addErrorToast } from 'components/organisms/Toasts';
import {
  getUniques,
  arrayIf,
  findObjectByValue,
} from 'utilities/array';
import { saveFilterQuery } from 'utilities/filtering';
import {
  isDefined,
  getItemSortBy,
} from 'utilities/item';
import {
  filterItems,
  getValueOptions,
} from 'utilities/itemFilter';
import { searchItems } from 'utilities/itemSearch';
import { objectGroupBy } from 'utilities/object';
import { get } from 'utilities/requests';
import {
  countsInternalGuidelinesPath,
  internalGuidelinePath,
  internalGuidelineSubscriptionsPath,
  newInternalGuidelinePath,
} from 'utilities/routes';
import { titleize } from 'utilities/string';
import {
  getPage,
  getParams,
  redirectWithParam,
  setParam,
} from 'utilities/url';

const optionProps = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
});

const definitionProps = PropTypes.shape({
  activeDefinitionCount: PropTypes.number.isRequired,
  brands: PropTypes.arrayOf(PropTypes.string).isRequired,
  channels: PropTypes.arrayOf(PropTypes.string).isRequired,
  id: PropTypes.number.isRequired,
  inactiveDefinitionCount: PropTypes.number.isRequired,
  markets: PropTypes.arrayOf(PropTypes.string).isRequired,
  updatedAt: PropTypes.string.isRequired,
});

const guidelineProps = PropTypes.shape({
  applicability: PropTypes.string.isRequired,
  brandCue: PropTypes.bool.isRequired,
  branded: PropTypes.bool.isRequired,
  global: PropTypes.bool.isRequired,
  guidelineType: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  isSubscribed: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  organizationOrder: PropTypes.number.isRequired,
  paid: PropTypes.bool.isRequired,
  samplingRate: PropTypes.number.isRequired,
  standard: PropTypes.bool.isRequired,
  state: PropTypes.string.isRequired,
});

const propTypes = {
  channelLookup: PropTypes.objectOf(PropTypes.string).isRequired,
  companyOptions: PropTypes.arrayOf(optionProps).isRequired,
  definitions: PropTypes.objectOf(definitionProps).isRequired,
  duplicateCompanyOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  filterSelections: filterProps,
  guidelines: PropTypes.arrayOf(guidelineProps).isRequired,
  isCookieCutter: PropTypes.bool,
  selectedCompanyId: PropTypes.number,
};

const defaultProps = {
  duplicateCompanyOptions: [],
  filterSelections: {},
  isCookieCutter: false,
  selectedCompanyId: undefined,
};

function getColumns(isTemplate, isCookieCutter) {
  return [
    {
      label: 'Actions',
      sortable: false,
      value: 'actions',
    },
    ...arrayIf(isCookieCutter,
      {
        label: 'Cookie\nCutter',
        sortable: false,
        value: 'duplicate',
      }),
    {
      label: 'ID',
      value: 'id',
    },
    {
      label: 'Name',
      value: 'name',
    },
    ...arrayIf(!isTemplate,
      {
        label: 'Subscribed',
        value: 'isSubscribed',
      }),
    {
      label: 'Definitions',
      value: 'definitionCount',
    },
    {
      label: 'Usage',
      value: 'usageCount',
    },
    {
      label: 'Applicability',
      value: 'applicability',
    },
  ];
}

const optionalColumns = [
  {
    label: 'Branded',
    value: 'branded',
  },
  {
    label: 'Global/Local',
    value: 'global',
  },
  {
    label: 'Guideline Role',
    value: 'guidelineRole',
  },
  {
    label: 'Guideline Type',
    value: 'guidelineType',
  },
  {
    label: 'Last Updated',
    value: 'updatedAt',
  },
  {
    label: 'Position',
    value: 'organizationOrder',
  },
  {
    label: 'Sampling Rate',
    value: 'samplingRate',
  },
  {
    label: 'Usage Reporting',
    value: 'paid',
  },
];

const filterDimensions = [
  {
    label: 'Applicability',
    value: 'applicability',
  },
  {
    label: 'Branded',
    value: 'branded',
  },
  {
    label: 'Brand',
    value: 'brands',
  },
  {
    label: 'Channel',
    value: 'channels',
  },
  {
    label: 'Guideline Type',
    value: 'guidelineType',
  },
  {
    label: 'Guideline Role',
    value: 'guidelineRole',
  },
  {
    label: 'Market',
    value: 'markets',
  },
  {
    label: 'Subscribed',
    value: 'isSubscribed',
  },
  {
    label: 'Usage Reporting',
    value: 'paid',
  },
];

const searchKeys = [
  'id',
  'name',
];

const sortDefault = {
  asc: true,
  key: 'organizationOrder',
};

const applicabilityLabels = {
  all: 'All',
  audit: 'In-Flight',
  pretest: 'Pre-Flight',
};

const loadingCountDimension = {
  element: <Icon color="grey" name="processing" />,
  value: 1, // A non-zero value prevents deletion of the guideline
};

function getHeaderLabel(label, isTemplate) {
  switch (label) {
    case 'Usage':
      return isTemplate ? 'Subscriptions' : 'Checks';
    default:
      return label;
  }
}

function getDefinitionsDimension(activeCount, inactiveCount) {
  if (!isDefined(activeCount) || !isDefined(inactiveCount)) return NULL_DIMENSION;

  return {
    label: `${activeCount} / ${inactiveCount}`,
    value: activeCount + inactiveCount,
  };
}

function getEligibilityDimension(jsons, lookup = {}) {
  const inputs = (jsons ?? []).map((json) => JSON.parse(json ?? '[]')).flat();
  const labels = getUniques(inputs).map((value) => lookup[value] ?? value);
  const options = labels.map((label) => ({
    label,
    value: label,
  }));

  return {
    label: labels.length === 1 ? labels[0] : 'Various',
    options,
    value: labels.join(', '),
  };
}

function getGuidelineRoleDimension(standard, brandCue) {
  const option = findRoleOption({
    brandCue,
    standard,
  });

  return {
    label: option.label,
    value: option.value,
  };
}

function getUsageCountDimension(isTemplate, id, subscriptionCount, checkCount) {
  if (!isTemplate) {
    if (!isDefined(checkCount)) return loadingCountDimension;

    return { value: checkCount };
  }

  if (!isDefined(subscriptionCount)) return loadingCountDimension;

  return {
    element: <a href={internalGuidelineSubscriptionsPath(id)}>{ subscriptionCount }</a>,
    value: subscriptionCount,
  };
}

function getStateDimension(state, activeDefinitionCount, checkCount, isSubcribed) {
  const isPreviousSubscription = state === 'editing' && isSubcribed;
  const isRetired = (state === 'active' || state === 'editing')
    && activeDefinitionCount === 0
    && checkCount > 0
    && !isSubcribed;

  return {
    value: (isPreviousSubscription || isRetired) ? 'retired' : state,
  };
}

function getItems(
  guidelines,
  definitions,
  counts,
  isTemplate,
  channelLookup,
  duplicateCompanyOptions,
) {
  return guidelines.map((guideline) => {
    const {
      applicability,
      brandCue,
      branded,
      id,
      isSubscribed,
      global,
      guidelineType,
      name,
      organizationOrder,
      paid,
      samplingRate,
      standard,
      state,
    } = guideline;
    const {
      activeDefinitionCount,
      brands,
      channels,
      inactiveDefinitionCount,
      markets,
      updatedAt,
    } = (definitions[id] ?? {});
    const {
      checkCount,
      subscriptionCount,
    } = (counts[id] ?? {});

    return {
      actions: {
        element: (
          <GuidelineActions
            definitionCount={activeDefinitionCount + inactiveDefinitionCount}
            guideline={guideline}
            usageCount={isTemplate ? subscriptionCount : checkCount}
          />
        ),
        value: NULL_VALUE,
      },
      applicability: {
        label: applicabilityLabels[applicability],
        value: applicability,
      },
      branded: getBoolDimension(branded, true),
      brands: getEligibilityDimension(brands),
      channels: getEligibilityDimension(channels, channelLookup),
      definitionCount: getDefinitionsDimension(activeDefinitionCount, inactiveDefinitionCount),
      duplicate: {
        element: (
          <DuplicateGuideline
            companies={duplicateCompanyOptions}
            id={guideline.id}
            name={guideline.name}
          />
        ),
        value: NULL_VALUE,
      },
      global: {
        label: global ? 'Global' : 'Local',
        value: global,
      },
      guidelineRole: getGuidelineRoleDimension(standard, brandCue),
      guidelineType: {
        label: parseGuidelineTypeLabel(guidelineType),
        value: guidelineType,
      },
      id: { value: id },
      isSubscribed: getBoolDimension(isSubscribed && state === 'active', true),
      markets: getEligibilityDimension(markets),
      name: {
        element: <a href={internalGuidelinePath(id)}>{ name }</a>,
        value: name,
      },
      organizationOrder: { value: organizationOrder },
      paid: getBoolDimension(paid, true),
      samplingRate: {
        label: `${samplingRate}%`,
        value: samplingRate,
      },
      state: getStateDimension(state, activeDefinitionCount, checkCount, isSubscribed),
      updatedAt: getDateDimension(updatedAt),
      usageCount: getUsageCountDimension(isTemplate, id, subscriptionCount, checkCount),
    };
  });
}

function getTabs(itemsByState, setSelectedTab) {
  const states = ['active', 'editing', 'retired', 'trial'];

  return states.map((value) => ({
    label: `${titleize(value)} (${itemsByState[value]?.length ?? 0})`,
    onClick: () => setSelectedTab(value),
    value,
  }));
}

function Guidelines({
  channelLookup,
  companyOptions,
  definitions,
  duplicateCompanyOptions,
  guidelines,
  filterSelections: initialFilterSelections,
  isCookieCutter,
  selectedCompanyId,
}) {
  const params = getParams(window);
  const [counts, setCounts] = useState({});
  const [lastCompanyId, setLastCompanyId] = useLocalStorage('cxIntCompanyId', null);
  const [selectedCompany, setSelectedCompany] = useState(
    findObjectByValue(companyOptions, selectedCompanyId),
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSelections, setFilterSelections] = useState(initialFilterSelections);
  const [search, setSearch] = useState('');
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useLocalStorage('cxIntGuidelineTab', 'active');
  const [selectedColumns, setSelectedColumns] = useLocalStorage('cxIntGuidelineColumns', []);
  const [sort, setSort] = useState(sortDefault);
  const [page, setPage] = useState(getPage(params));
  const isTemplate = selectedCompany?.value === null;
  const isLoading = selectedCompanyId !== selectedCompany?.value;

  const handleCompanyChange = (option) => {
    setSelectedCompany(option);
    setLastCompanyId(option?.value);
    redirectWithParam('company_id', option.value ?? '', params, window);
  };

  useEffect(() => {
    const getCounts = async () => {
      const url = countsInternalGuidelinesPath({ company_id: selectedCompanyId });
      const { data, isError } = await get(url);

      if (!isError) {
        setCounts(data);
      } else {
        addErrorToast('Could not load usage counts. Please try again later');
      }
    };

    getCounts();
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId && lastCompanyId) {
      const found = findObjectByValue(companyOptions, lastCompanyId);

      if (found) {
        handleCompanyChange(found);
      }
    } else if (selectedCompanyId && lastCompanyId && selectedCompanyId !== lastCompanyId) {
      setLastCompanyId(selectedCompanyId);
    }
  }, [selectedCompanyId, companyOptions, lastCompanyId]);

  const headers = useMemo(() => {
    const allColumns = [...getColumns(isTemplate, isCookieCutter), ...selectedColumns];

    return allColumns.map(({ label, value }) => ({
      key: value,
      label: getHeaderLabel(label, isTemplate),
    }));
  }, [isTemplate, isCookieCutter, selectedColumns]);

  const items = useMemo(() => (
    getItems(guidelines, definitions, counts, isTemplate, channelLookup, duplicateCompanyOptions)
  ), [guidelines, isTemplate, definitions, counts, channelLookup, duplicateCompanyOptions]);

  const filterOptions = useMemo(() => (
    getValueOptions(filterDimensions, items)
  ), [items]);

  const filteredItems = useMemo(() => (
    filterItems(items, filterSelections)
  ), [items, filterSelections]);

  const searchedItems = useMemo(() => {
    setPage(1);
    return searchItems(filteredItems, search, searchKeys);
  }, [filteredItems, search]);

  const tabbedItems = useMemo(() => {
    const itemsByState = objectGroupBy(searchedItems, (item) => item.state.value);
    setTabs(getTabs(itemsByState, setSelectedTab));

    return itemsByState;
  }, [searchedItems]);

  const sortedItems = useMemo(() => {
    const tabItems = tabbedItems[selectedTab]?.slice() ?? [];
    const byKeyDir = getItemSortBy(sort.key, sort.asc);

    return tabItems.sort(byKeyDir);
  }, [tabbedItems, selectedTab, sort]);

  const handleAdd = () => {
    setSelectedTab('editing');
    if (isTemplate) {
      window.location.replace(newInternalGuidelinePath());
    } else {
      window.location.replace(newInternalGuidelinePath({ company_id: selectedCompany?.value }));
    }
  };

  const handleFilterSelect = async (value) => {
    setPage(1);
    setFilterSelections(value);

    if (Object.keys(value).length > 0) {
      const uuid = await saveFilterQuery(value);
      setParam('filter_uuid', uuid, params, window);
    } else {
      setParam('filter_uuid', '', params, window);
    }
  };

  const getTabLabel = (selected) => (
    tabs.find(({ value }) => value === selected)?.label
  );

  return (
    <Card className="u-flexColumn u-gap-24">
      <Dropdown
        label="Company"
        menuProps={{ size: 'medium' }}
        options={companyOptions}
        selected={selectedCompany}
        size="medium"
        onChange={handleCompanyChange}
      />
      <Divider />
      <div className="u-flexColumn u-gap-16">
        <div className="u-flexRow u-justifyBetween u-alignEnd u-gap-16">
          <div className="u-flexRow u-alignCenter u-gap-8">
            <ItemsFilter
              dimensions={filterDimensions}
              isOpen={filterOpen}
              options={filterOptions}
              selections={filterSelections}
              onClose={() => setFilterOpen(false)}
              onOpen={() => setFilterOpen(true)}
              onSelect={handleFilterSelect}
            />
            <FilterTags
              dimensions={filterDimensions}
              selections={filterSelections}
              onClick={() => setFilterOpen(true)}
              onRemove={handleFilterSelect}
            />
          </div>
          <AddNewButton
            label="Add Guideline"
            onClick={handleAdd}
          />
        </div>
        <div className="u-flexRow u-alignEnd u-gap-16">
          <Search
            placeholder="Search by Name, ID"
            value={search}
            onChange={setSearch}
          />
          <MultipleDropdown
            label="Optional Columns"
            options={optionalColumns}
            selected={selectedColumns}
            onChange={(option) => setSelectedColumns((last) => toggleObject(last, option))}
          />
          <Button
            disabled={selectedColumns.length === 0}
            label="Reset Columns"
            variant="tertiary"
            onClick={() => setSelectedColumns([])}
          />
        </div>
      </div>
      { isLoading && (
        <div className="u-marginTop-24">
          <TableSkeleton animate />
        </div>
      ) }
      { !isLoading && (
        <div>
          <Tabs
            currentTab={getTabLabel(selectedTab)}
            tabs={tabs}
            variant="button"
            showSingleTab
          />
          <ItemsTable
            className="u-scrollShadowRight"
            emptyTableContent={emptyState}
            headers={headers}
            items={sortedItems}
            page={page}
            sort={sort}
            onPageChange={setPage}
            onSortChange={setSort}
          />
        </div>
      ) }
    </Card>
  );
}

Guidelines.propTypes = propTypes;
Guidelines.defaultProps = defaultProps;

export default Guidelines;
